import { Button, Flex, Heading, useDisclosure, VStack } from '@ritualco/jutsu'
import { AddIcon } from '@chakra-ui/icons'
import { FullWidthPage } from 'components/PageTemplates'
import { Pagination } from 'components/Pagination'
import { MemberCreateModal } from 'components/Modals/MemberCreateModal'
import { useContent } from 'lib/content/react'
import {
  useCreateWorkspaceAdmin,
  useDeleteWorkspaceAdmin,
  useTeamMembersQuery,
} from 'api/endpoints/portalAdmins'
import { useState } from 'react'
import { PortalAdminRole } from 'lib/generated/api'
import { TeamTable } from 'components/Tables/TeamTable'
import Loading from 'components/Loading'
import { INITIAL_ROWS_PER_PAGE } from 'config'

export type TeamProps = {
  organizationId: string
  workspaceId: string
}

const Team = ({ workspaceId, organizationId }: TeamProps) => {
  const { formatMessage } = useContent()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const [paginationTokenOption, setPaginationTokenOption] = useState({})
  const { isLoading, data } = useTeamMembersQuery({
    organizationId,
    workspaceId,
    limit: rowsPerPage,
    ...paginationTokenOption,
  })
  const invite = useCreateWorkspaceAdmin()
  const deleteAdmin = useDeleteWorkspaceAdmin()

  if (isLoading) {
    return <Loading />
  }

  const roles = [
    {
      label: formatMessage({ id: 'pmportal.settings.team.adminLabel' }),
      value: PortalAdminRole.Administrator,
      description: formatMessage({
        id: 'pmportal.settings.team.adminDescription',
      }),
    },
    {
      label: formatMessage({ id: 'pmportal.settings.team.viewerLabel' }),
      value: PortalAdminRole.ViewOnly,
      description: formatMessage({
        id: 'pmportal.settings.team.viewerDescription',
      }),
    },
  ]

  return (
    <FullWidthPage
      heading={
        <Heading size="4xl">
          {formatMessage({ id: 'pmportal.settings.team.title' })}
        </Heading>
      }
      subheading={formatMessage({ id: 'pmportal.settings.team.subtitle' })}
    >
      <VStack spacing={2}>
        {/* TODO: Button and Modal only visible for Admin users of this workspace */}
        <Flex justify="flex-end" mt={4} w="100%">
          <Button onClick={onOpen} leftIcon={<AddIcon />}>
            {formatMessage({ id: 'pmportal.settings.team.newMember' })}
          </Button>
          <MemberCreateModal
            isOpen={isOpen}
            onClose={onClose}
            onInvite={({ emails, role }) => {
              invite.mutate({
                organizationId,
                workspaceId,
                portalAdmin: emails.map(email => ({
                  email,
                  role,
                })),
              })
            }}
            roles={roles}
          />
        </Flex>
        <TeamTable
          members={data?.results}
          onRemove={portalAdminId => {
            deleteAdmin.mutate({
              organizationId,
              workspaceId,
              portalAdminId,
            })
          }}
        />
        <Pagination
          rowsPerPage={rowsPerPage}
          hasNext={data?.hasNextPage}
          hasPrev={data?.hasPreviousPage}
          onRowsPerPageChange={e => setRowsPerPage(Number(e.target.value))}
          onPrev={() =>
            setPaginationTokenOption({
              previousPageToken: data?.paginationToken,
            })
          }
          onNext={() =>
            setPaginationTokenOption({ nextPageToken: data?.paginationToken })
          }
        />
      </VStack>
    </FullWidthPage>
  )
}

export default Team
